/**
 * Where the reading pane sits, and how big the message list is beside or
 * above it. Kept on this device, like the theme (`theme.ts`): a good list
 * width depends on the screen, not on the person. The desktop app keeps its
 * screens' address from one start to the next, so it remembers too.
 *
 * One store for the whole page: the account menu changes the pane, the mail
 * view shows it, and neither passes it to the other.
 */

import { useSyncExternalStore } from 'react';

/** Beside the list, below it, or not at all (the message takes the list's place). */
export type Pane = 'right' | 'bottom' | 'off';

export const PANES: readonly Pane[] = ['right', 'bottom', 'off'];

export const PANE_LABEL: Record<Pane, string> = {
  right: 'Right',
  bottom: 'Bottom',
  off: 'Off',
};

export interface Layout {
  readonly pane: Pane;
  /** The list's width with the pane on the right, in pixels. */
  readonly listWidth: number;
  /** The list's height with the pane at the bottom, in pixels. */
  readonly listHeight: number;
}

export const DEFAULT_LAYOUT: Layout = { pane: 'right', listWidth: 380, listHeight: 320 };

/** The list and the reading pane never get smaller than these. */
export const MIN_LIST_WIDTH = 260;
export const MIN_READER_WIDTH = 360;
export const MIN_LIST_HEIGHT = 140;
export const MIN_READER_HEIGHT = 200;

const STORAGE_KEY = 'zitimail.layout';

function read(): Layout {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_LAYOUT;
    }
    const parsed = JSON.parse(raw) as Partial<Layout>;
    const size = (v: unknown, fallback: number, min: number) =>
      typeof v === 'number' && Number.isFinite(v) ? Math.max(min, Math.round(v)) : fallback;
    return {
      pane: PANES.includes(parsed.pane as Pane) ? (parsed.pane as Pane) : DEFAULT_LAYOUT.pane,
      listWidth: size(parsed.listWidth, DEFAULT_LAYOUT.listWidth, MIN_LIST_WIDTH),
      listHeight: size(parsed.listHeight, DEFAULT_LAYOUT.listHeight, MIN_LIST_HEIGHT),
    };
  } catch {
    // An unreadable preference is not worth failing the page over.
    return DEFAULT_LAYOUT;
  }
}

let current: Layout | null = null;
const listeners = new Set<() => void>();

function snapshot(): Layout {
  current ??= read();
  return current;
}

/** Changes the layout everywhere on the page, and remembers it. */
export function setLayout(change: Partial<Layout>): void {
  current = { ...snapshot(), ...change };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    // Private browsing can refuse writes; the layout still applies.
  }
  listeners.forEach((l) => l());
}

/** For tests: forget what was read, so the next read is from storage. */
export function resetLayoutForTests(): void {
  current = null;
}

export function useLayout(): Layout {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    snapshot,
    () => DEFAULT_LAYOUT,
  );
}

/** `value` kept between `min` and `max` (and `min` wins when they cross). */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}
